// markdownListContinuation — Enter-key list continuation for the issue
// description textarea, kept in a non-component module so the toolbar component
// file stays component-only (react-refresh). A non-empty "- " / "N. " line
// inserts the next marker on Enter; an empty marker line ends the list;
// Shift+Enter is a plain newline.

import type { KeyboardEvent } from "react";

const BULLET_LINE = /^(\s*)-\s+(.*)$/;
const ORDERED_LINE = /^(\s*)(\d+)\.\s+(.*)$/;

export function handleMarkdownEnter(
  e: KeyboardEvent<HTMLTextAreaElement>,
  value: string,
  onChange: (next: string) => void,
): void {
  if (e.key !== "Enter" || e.shiftKey) return;
  const ta = e.currentTarget;
  const pos = ta.selectionStart;
  if (pos !== ta.selectionEnd) return; // only with a collapsed caret
  const lineStart = value.lastIndexOf("\n", pos - 1) + 1;
  const line = value.slice(lineStart, pos);

  const bullet = BULLET_LINE.exec(line);
  const ordered = ORDERED_LINE.exec(line);
  if (!bullet && !ordered) return;

  e.preventDefault();
  const indent = (bullet ?? ordered)![1];
  const content = (bullet ? bullet[2] : ordered![3]).trim();

  // Empty item → end the list: drop the marker from this line.
  if (content === "") {
    const next = value.slice(0, lineStart) + value.slice(pos);
    onChange(next);
    requestAnimationFrame(() => ta.setSelectionRange(lineStart, lineStart));
    return;
  }

  const marker = bullet ? "- " : `${parseInt(ordered![2], 10) + 1}. `;
  const insert = `\n${indent}${marker}`;
  const next = value.slice(0, pos) + insert + value.slice(pos);
  onChange(next);
  const caret = pos + insert.length;
  requestAnimationFrame(() => ta.setSelectionRange(caret, caret));
}
